import axios from 'axios';

// Send the session cookie on every request by default — previously only
// set per-call on some endpoints (auth/smart-intake/learning), which meant
// procurement/document/lookup calls silently omitted the cookie and 401'd.
axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Health check
  health: () => axios.get(`${API}/health`),
  
  // Policy
  getPolicy: () => axios.get(`${API}/policy`),
  
  // Locations
  getLocations: () => axios.get(`${API}/locations`),
  getLocation: (locationId) => axios.get(`${API}/locations/${locationId}`),
  
  // BOQ and Document uploads
  uploadBoq: (procurementId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/procurement/${procurementId}/upload-boq`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  uploadDocument: (procurementId, file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    return axios.post(`${API}/procurement/${procurementId}/upload-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  deleteDocument: (procurementId, documentId) =>
    axios.delete(`${API}/procurement/${procurementId}/document/${documentId}`),

  // Remove a SERVER-side BOQ (Ashok ruling 2026-09-03: delete regenerates,
  // exactly as an intake edit does). The server writes the fresh shape
  // (boq_items: [], no boq_source key - what a draft that never had a BOQ
  // stores) and nothing else - regeneration is the hash's job, not this
  // call's. See routes/documents.py delete_boq.
  deleteBoq: (procurementId) =>
    axios.delete(`${API}/procurement/${procurementId}/boq`),

  downloadDocument: (procurementId, documentId) => 
    `${API}/procurement/${procurementId}/document/${documentId}/download`,
  
  getBoqTemplate: () => axios.get(`${API}/boq-template`),
  
  // Procurement endpoints

  // THE CREATION CONTRACT, in three calls (Intake Compression V1, Ashok
  // ruling 2026-08-20): createDraft -> uploadBoq/uploadDocument -> parseIntake.
  //
  // createDraft persists the requirement and allocates the serial WITHOUT
  // parsing, because the upload endpoints below take a procurement_id in
  // the path - the draft is what gives the buyer somewhere to attach to.
  // parseIntake then analyses the requirement together with whatever was
  // attached, which is the entire point of the reordering.
  createDraft: (userRequirement) =>
    axios.post(`${API}/procurement/draft`, { user_requirement: userRequirement }),

  // Sends the CURRENT textarea contents so a buyer who kept typing while
  // attaching files gets what is on screen parsed. The backend persists it
  // before running the agent, so a failed parse keeps it.
  parseIntake: (procurementId, userRequirement) =>
    axios.post(`${API}/procurement/${procurementId}/parse`, { user_requirement: userRequirement }),

  // STATELESS (Intake Compression V1, 2026-08-21). Takes no procurement_id
  // and persists nothing: it exists so the merged Intake screen can show a
  // buyer their BOQ lines before any draft is created. Same parse, same
  // row ids, same error shapes as uploadBoq - see routes/documents.py's
  // preview_boq. The FILE is held in the browser until Continue.
  previewBoq: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/boq-preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Create-and-parse in one call. NOT used by the wizard, which drafts,
  // attaches and then parses; retained for callers with no files.
  createIntake: (userRequirement) =>
    axios.post(`${API}/procurement/intake`, { user_requirement: userRequirement }),

  // categoryOverride (optional): zero-floor category guard (PROC-000026
  // family) - the forced category-selection path calls this with the
  // buyer's chosen code instead of a re-parse; backend bypasses
  // intake_agent entirely on this path.
  //
  // decisionContext: REQUIRED whenever categoryOverride is passed, one of
  // 'forced_low_signal' | 'voluntary_change' (Ashok ruling, 2026-08-04).
  // Two distinct buyer events share this endpoint and the backend refuses
  // to infer which one is happening - it cross-checks this declaration
  // against the stored category_confidence and 400s on disagreement, so
  // an omitted or wrong value fails loudly rather than mislabelling the
  // decision. Deliberately NOT defaulted here: a default in the wrapper
  // would be inference wearing a different hat, one layer further from
  // the check. Callers state their intent (2 of them: WizardContext's
  // handleCategoryOverride for the forced gate, handleCategoryChange for
  // the passive change link). Omitted entirely on the ordinary re-parse
  // path, which passes no categoryOverride and decides no category.
  updateIntake: (procurementId, userRequirement, categoryOverride, decisionContext) =>
    axios.post(`${API}/procurement/${procurementId}/update-intake`, {
      user_requirement: userRequirement,
      ...(categoryOverride ? { category_override: categoryOverride } : {}),
      ...(categoryOverride ? { decision_context: decisionContext } : {}),
    }),

  // HEADER NO-FIT (F1 / C1b-1b). Its own wrapper rather than three more
  // optional arguments on updateIntake above: a no-fit carries NO category
  // override, so every one of that function's spreads would have to be
  // re-reasoned about, and a caller passing the wrong combination would
  // send a payload the model validator rejects. One name, one shape.
  //
  // THE CLIENT SENDS ONE FIELD. The request number, the requestor and the
  // timestamp are the server's, and a client-supplied copy would be a
  // second producer of a record whose entire purpose is to say who asked
  // for what, and when.
  declareHeaderCategoryNoFit: (procurementId, userRequirement, requestedText) =>
    axios.post(`${API}/procurement/${procurementId}/update-intake`, {
      user_requirement: userRequirement,
      decision_context: 'no_category_fits',
      category_no_fit_text: requestedText,
    }),

  generateQuestions: (procurementId) =>
    axios.post(`${API}/procurement/${procurementId}/generate-questions`),
  
  // businessQuestions (C1b-0): THE LIST THESE ANSWERS WERE KEYED AGAINST.
  // answers are a positional map - "q3" means "the answer to
  // businessQuestions[3]" - and question edits live only in client state
  // until finalize-questions, so submitting answers without the list they
  // were numbered against let the server persist NEW-indexed answers
  // beside the OLD stored list. Sent here so the backend writes both
  // halves in one $set. Optional server-side for an older client; every
  // caller in this file passes it.
  submitAnswers: (procurementId, answers, businessQuestions) => 
    axios.post(`${API}/procurement/${procurementId}/submit-answers`, {
      procurement_id: procurementId,
      answers,
      ...(businessQuestions !== undefined ? { business_questions: businessQuestions } : {}),
    }),
  
  finalizeQuestions: (procurementId, finalQuestions, businessQuestions, force = false) =>
    axios.post(`${API}/procurement/${procurementId}/finalize-questions`, {
      procurement_id: procurementId,
      final_questions: finalQuestions,
      // Sent for the §5 human-vs-machine question edit-delta capture; the
      // backend diffs these against the AI baseline (never drives refinement).
      business_questions: businessQuestions,
      force
    }),
  
  finalizeLineItems: (procurementId, lineItems, currency) =>
    axios.post(`${API}/procurement/${procurementId}/finalize-line-items`, {
      procurement_id: procurementId,
      line_items: lineItems,
      rfp_currency: currency
    }),
  
  generateRfp: (procurementId) => 
    axios.post(`${API}/procurement/${procurementId}/generate-rfp`),
  
  assessRisk: (procurementId) =>
    axios.post(`${API}/procurement/${procurementId}/assess-risk`),

  // The buyer's answer to the risk-stage fork: 'as_issued' | 'regenerate' |
  // 'cancel'. Every arm is recorded server-side (staleness gate, 2026-09-15).
  recordRiskCategoryAttestation: (procurementId, decision) =>
    axios.post(`${API}/procurement/${procurementId}/risk-category-attestation`, { decision }),
  
  getProcurement: (procurementId) => 
    axios.get(`${API}/procurement/${procurementId}`),
  
  // PAGED, SERVER-SIDE (2026-09-01). Returns the envelope
  // {items, total, page, page_size, group} - the endpoint used to return a
  // bare array capped at 100, which was already short for one real owner.
  getAllProcurements: ({ page = 1, pageSize = 25, group = null } = {}) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (group) params.set('group', group);
    return axios.get(`${API}/procurements?${params.toString()}`);
  },

  // The dashboard tiles' one source of truth: per-group count and USD sum
  // over EVERY document this user owns, never over a page.
  getProcurementStats: () => axios.get(`${API}/procurements/stats`),
  
  deleteProcurement: (procurementId) => 
    axios.delete(`${API}/procurement/${procurementId}`),
  
  downloadRfp: (procurementId) =>
    `${API}/procurement/${procurementId}/download-rfp`,

  // PREVIEW PARITY REWORK (2026-07-28 ruling): the on-screen preview's
  // governed title + Executive Summary facts - computed server-side by
  // the SAME resolvers the downloaded .docx uses, so the preview can
  // never diverge from what a buyer downloads.
  getRfpPreviewSummary: (procurementId) =>
    axios.get(`${API}/procurement/${procurementId}/rfp-preview-summary`),

  // Get category and location lookups
  getLookups: () =>
    axios.get(`${API}/procurement/lookups`),

  // Wizard audit trail (spec P2 §4/§10) - viewed/edit_unlocked/edited/
  // regenerated/regeneration_skipped/forced_regeneration/restored
  postWizardEvent: (procurementId, step, event, metadata) =>
    axios.post(`${API}/procurement/${procurementId}/wizard-event`, { step, event, metadata }),
  
  // ============================================================================
  // ADMIN AUTHENTICATION
  // ============================================================================
  
  adminLogin: (username, password) => 
    axios.post(`${API}/admin/login`, { username, password }),
  
  adminLogout: () => 
    axios.post(`${API}/admin/logout`, {}, { headers: getAuthHeader() }),
  
  verifyAdmin: () => 
    axios.get(`${API}/admin/verify`, { headers: getAuthHeader() }),
  
  // ============================================================================
  // MASTER DATA - LOCATIONS
  // ============================================================================
  
  getMasterLocations: () => 
    axios.get(`${API}/master/locations`),
  
  uploadMasterLocations: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/master/locations/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // F14: DELETE now DEACTIVATES (the row is retained). Name kept so no
  // caller breaks; the pair below reinstates.
  deleteMasterLocation: (locationId) =>
    axios.delete(`${API}/master/locations/${locationId}`, { headers: getAuthHeader() }),

  activateMasterLocation: (locationId) =>
    axios.put(`${API}/master/locations/${locationId}/activate`, {}, { headers: getAuthHeader() }),
  
  // ============================================================================
  // MASTER DATA - CATEGORIES
  // ============================================================================
  
  // scope_elements (admin-only master data) is only included in the
  // response when an admin bearer token is sent - non-admin callers of
  // these same endpoints (wizard dropdowns, category browse) get every
  // other field but never that one, enforced server-side.
  // purpose='selection' (B2b, 2026-07-30): the Lines-step full-master
  // category dropdown - returns ACTIVE categories only. Omit (default
  // browse) for display/admin-table callers, which need every row
  // including deactivated ones.
  getMasterCategories: (purpose) =>
    axios.get(`${API}/master/categories`, { params: purpose ? { purpose } : undefined, headers: getAuthHeader() }),

  searchMasterCategories: (query, flag) =>
    axios.get(`${API}/master/categories/search`, { params: { q: query, flag }, headers: getAuthHeader() }),
  
  suggestCategories: (requirement) => 
    axios.get(`${API}/master/categories/suggest`, { params: { requirement } }),
  
  uploadMasterCategories: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/master/categories/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  
  deleteMasterCategory: (categoryId) =>
    axios.delete(`${API}/master/categories/${categoryId}`, { headers: getAuthHeader() }),

  activateMasterCategory: (categoryId) =>
    axios.put(`${API}/master/categories/${categoryId}/activate`, {}, { headers: getAuthHeader() }),

  // Item 4a (2026-07-21) - the Lines step's "Suggested" category tier.
  // Distinct from suggestMasterCategories above (different endpoint,
  // different response shape - top-5 {custom_code, category_name, score}).
  suggestCategoriesForLineItem: (text) =>
    axios.get(`${API}/categories/suggest`, { params: { text } }),

  // ============================================================================
  // MASTER DATA - CURRENCIES
  // ============================================================================
  
  // purpose='selection' (B2b, 2026-07-30): the rfp_currency answer
  // dropdown - ACTIVE currencies only. Omit for display/admin-table
  // callers, which need every row including deactivated ones.
  getMasterCurrencies: (purpose) =>
    axios.get(`${API}/master/currencies`, { params: purpose ? { purpose } : undefined }),
  
  uploadMasterCurrencies: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/master/currencies/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // ============================================================================
  // MASTER DATA - UOM (UNIT OF MEASURE)
  // ============================================================================
  // Item 2 (2026-07-21 sweep) - controlled UoM vocabulary, same pattern as
  // categories/currencies above.

  // POST-LAUNCH (role-scoped master data visibility): the backend now
  // strips measurement_family/unit_semantics for non-admin callers (same
  // pattern as categories' scope_elements) - the admin token must be sent
  // here too, or the admin master-data table would itself see the
  // stripped response and never show its own admin-only columns. This is
  // the BROWSE variant (admin master-data table) - never use it for
  // SARU/validation logic, use getMasterUomsForValidation below instead.
  // purpose='selection' (B2b, 2026-07-30): Lines-step/SmartAnswerInput's
  // quantity uom dropdowns - ACTIVE uoms only.
  getMasterUoms: (purpose) =>
    axios.get(`${API}/master/uoms`, { params: purpose ? { purpose } : undefined, headers: getAuthHeader() }),

  // VERIFICATION FIX (post-596050b): SARU (Stated-vs-Answered Unit
  // dimension check, StepAnswers.jsx -> statedIntentValidationRules.js)
  // needs measurement_family/unit_semantics for EVERY buyer, admin or
  // not - it's derivation logic the wizard runs internally, never a
  // rendered master-data column. purpose=validation always returns the
  // full row server-side, regardless of admin status - use this (never
  // getMasterUoms) for any fetch feeding SARU or similar validation.
  getMasterUomsForValidation: () =>
    axios.get(`${API}/master/uoms`, { params: { purpose: 'validation' } }),

  uploadMasterUoms: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/master/uoms/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },

  deleteMasterUom: (uomId) =>
    axios.delete(`${API}/master/uoms/${uomId}`, { headers: getAuthHeader() }),

  activateMasterUom: (uomId) =>
    axios.put(`${API}/master/uoms/${uomId}/activate`, {}, { headers: getAuthHeader() }),

  // ============================================================================
  // MASTER DATA - EXCHANGE RATES (B2-A, stage 1 of 3 - Admin only, inert)
  // ============================================================================
  // No non-admin consumer exists yet (unlike currencies/uoms above) - both
  // calls always send the admin auth header, not just the upload.

  getMasterExchangeRates: () =>
    axios.get(`${API}/master/exchange-rates`, { headers: getAuthHeader() }),

  uploadMasterExchangeRates: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/master/exchange-rates/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },

  // ============================================================================
  // MASTER DATA - POLICY
  // ============================================================================
  
  getMasterPolicy: (policyType = 'procurement') =>
    axios.get(`${API}/master/policy`, { params: { policy_type: policyType } }),

  uploadMasterPolicy: (file, policyType = 'procurement') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('policy_type', policyType);
    return axios.post(`${API}/master/policy/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },

  getPolicyHistory: (policyType = null) =>
    axios.get(`${API}/master/policy/history`, { params: policyType ? { policy_type: policyType } : {} }),

  activatePolicy: (policyId) =>
    axios.put(`${API}/master/policy/${policyId}/activate`, {}, { headers: getAuthHeader() }),

  // Plain URLs (not axios calls) for <a href>/<iframe src> - GET-only,
  // served directly by the browser rather than fetched via JS.
  getPolicyFileDownloadUrl: (policyId) => `${API}/master/policy/${policyId}/download`,
  getPolicyFileViewUrl: (policyId) => `${API}/master/policy/${policyId}/view`,

  // ============================================================================
  // RISK FRAMEWORK
  // ============================================================================
  
  getRiskDomains: () => 
    axios.get(`${API}/risk/risk-domains`),
  
  getRiskDomain: (domainId) => 
    axios.get(`${API}/risk/risk-domains/${domainId}`),
  
  updateRiskDomain: (domainId, domainData) => 
    axios.put(`${API}/risk/risk-domains/${domainId}`, domainData, { headers: getAuthHeader() }),
  
  resetRiskDomainsToDefaults: () => 
    axios.post(`${API}/risk/risk-domains/reset-defaults`, {}, { headers: getAuthHeader() }),
  
  getSpendThresholds: () => 
    axios.get(`${API}/risk/spend-thresholds`),
  
  updateSpendThresholds: (thresholds) =>
    axios.put(`${API}/risk/spend-thresholds`, thresholds, { headers: getAuthHeader() }),

  // Scoped to db.spend_thresholds ONLY - the risk-domains reset is a
  // separate endpoint/wrapper (resetRiskDomainsToDefaults above) and
  // neither touches the other's collection.
  resetSpendThresholdsToDefaults: () =>
    axios.post(`${API}/risk/spend-thresholds/reset-defaults`, {}, { headers: getAuthHeader() }),
  
  getRiskThresholds: () =>
    axios.get(`${API}/risk/risk-thresholds`),

  // getCategoryDomainMapping REMOVED (B3, 2026-07-30) - the backend
  // endpoint it called (/risk/category-domain-mapping) is retired; this
  // wrapper was never called from any component (confirmed via grep).

  // ============================================================================
  // USER AUTHENTICATION (Google OAuth)
  // ============================================================================

  // Exchange Google OAuth code for app session
  googleCallback: (code, state) =>
    axios.post(`${API}/auth/google/callback`, { code, state }, { withCredentials: true }),

  // Local dev-only auth bypass (only works if DEV_AUTH_BYPASS=true on backend)
  devLogin: () =>
    axios.post(`${API}/auth/dev-login`, {}, { withCredentials: true }),

  // Get current authenticated user
  getAuthMe: () => 
    axios.get(`${API}/auth/me`, { withCredentials: true }),
  
  // Logout user
  logout: () => 
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }),
  
  // Check auth status
  checkAuth: () => 
    axios.get(`${API}/auth/check`, { withCredentials: true }),
  
  // Give consent for beta data sharing
  giveConsent: () =>
    axios.post(`${API}/auth/consent`, {}, { withCredentials: true }),

  // First-login role capture (Item 5, 2026-07-21)
  captureRole: (role, roleOtherText) =>
    axios.post(`${API}/auth/role`, { role, role_other_text: roleOtherText }, { withCredentials: true }),

  // Submit user feedback
  submitFeedback: (data) => 
    axios.post(`${API}/feedback`, data, { withCredentials: true }),

  // ============================================================================
  // SMART INTAKE - Specialized Buyer Agents
  // ============================================================================

  // Analyze requirement using Smart Intake
  smartIntakeAnalyze: (userRequirement, attachedDocuments = null, boqItems = null) =>
    axios.post(`${API}/smart-intake/analyze`, {
      user_requirement: userRequirement,
      attached_documents: attachedDocuments,
      boq_items: boqItems
    }, { withCredentials: true }),

  // Confirm smart intake analysis
  smartIntakeConfirm: (sessionId, userAnswers, confirmedFields = null) =>
    axios.post(`${API}/smart-intake/${sessionId}/confirm`, {
      user_answers: userAnswers,
      confirmed_fields: confirmedFields
    }, { withCredentials: true }),

  // Resolve a conflict in smart intake
  smartIntakeResolveConflict: (sessionId, field, chosenValue, chosenSource) =>
    axios.post(`${API}/smart-intake/${sessionId}/resolve-conflict`, {
      field,
      chosen_value: chosenValue,
      chosen_source: chosenSource
    }, { withCredentials: true }),

  // Create procurement from confirmed smart intake
  smartIntakeCreateProcurement: (sessionId) =>
    axios.post(`${API}/smart-intake/${sessionId}/create-procurement`, {}, { withCredentials: true }),

  // Get smart intake session details
  getSmartIntakeSession: (sessionId) =>
    axios.get(`${API}/smart-intake/${sessionId}`, { withCredentials: true }),

  // ============================================================================
  // ADMIN - USER CREDIT MANAGEMENT
  // ============================================================================

  // Get all users (admin only)
  getAdminUsers: () => {
    const token = localStorage.getItem('admin_token');
    return axios.get(`${API}/admin/users`, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Update user credits (admin only)
  updateUserCredits: (email, credits, addMode = false) => {
    const token = localStorage.getItem('admin_token');
    return axios.post(`${API}/admin/users/credits`, {
      email,
      credits,
      add_mode: addMode
    }, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Make user an owner (admin only)
  makeUserOwner: (email) => {
    const token = localStorage.getItem('admin_token');
    return axios.post(`${API}/admin/users/make-owner?email=${encodeURIComponent(email)}`, {}, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Get a specific user's procurements (admin only)
  getUserProcurements: (userId) => {
    const token = localStorage.getItem('admin_token');
    return axios.get(`${API}/admin/users/${userId}/procurements`, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Reset a user's beta window (admin only)
  resetUserBeta: (userId) => {
    const token = localStorage.getItem('admin_token');
    return axios.post(`${API}/admin/users/${userId}/reset-beta`, {}, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Create a beta account and mint its invite link (admin only).
  // THE RESPONSE IS THE ONLY PLACE THE LINK EVER APPEARS - the server
  // stores a hash, so there is no endpoint that can return it again.
  createInvite: (email, name) => {
    const token = localStorage.getItem('admin_token');
    return axios.post(`${API}/admin/invites`, { email, name }, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Outstanding invites (admin only). Carries no token material.
  getInvites: () => {
    const token = localStorage.getItem('admin_token');
    return axios.get(`${API}/admin/invites`, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Sign in with email and password. PUBLIC. The server returns one
  // generic refusal for every failure reason, so the UI must not try to
  // interpret WHY - it displays what it is given.
  passwordLogin: (email, password) =>
    axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true }),

  // Mint a single-use password-reset link for an existing account (admin
  // only). Same one-shot display rule as an invite.
  createPasswordReset: (email) => {
    const token = localStorage.getItem('admin_token');
    return axios.post(`${API}/admin/password-resets`, { email }, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Redeem an admin-issued reset link. PUBLIC.
  resetPassword: (token, password) =>
    axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true }),

  // Redeem an invite and set the account's password. PUBLIC - the token in
  // the link is the only credential the caller has. The server issues a
  // session identical in shape to a Google-issued one.
  acceptInvite: (token, password) =>
    axios.post(`${API}/auth/accept-invite`, { token, password }, { withCredentials: true }),

  // Revoke a user's outstanding invite (admin only)
  revokeInvite: (userId) => {
    const token = localStorage.getItem('admin_token');
    return axios.post(`${API}/admin/invites/${userId}/revoke`, {}, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // All requests across every user (Item 3c, admin only) - serial, title,
  // owner name/email, status, updated_at.
  // PAGED (2026-09-01): the server-side `.to_list(length=1000)` cap is gone.
  getAllProcurementsAdmin: ({ page = 1, pageSize = 100 } = {}) => {
    const token = localStorage.getItem('admin_token');
    return axios.get(`${API}/admin/procurements?page=${page}&page_size=${pageSize}`, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Outstanding master-data requests (F1 / C1b-1b, admin only). DERIVED
  // from the procurement documents on every call - there is no stored list
  // to go stale, so this needs no refresh discipline beyond re-fetching.
  getMasterDataRequests: () => {
    const token = localStorage.getItem('admin_token');
    return axios.get(`${API}/admin/master-data-requests`, {
      headers: { 'X-Admin-Token': token },
      withCredentials: true
    });
  },

  // Record an administrator's ruling on one master-data request (F1 /
  // C1b-1b, B2a). IMMUTABLE: a second call for the same request number is
  // refused with a 409 naming the ruling that already stands.
  //
  // The request number is the whole address. The admin never writes into a
  // procurement document, so no procurement id is sent, and none is needed.
  recordMasterDataRequestDisposition: (requestNumber, disposition, comment) => {
    const token = localStorage.getItem('admin_token');
    return axios.post(
      `${API}/admin/master-data-requests/${requestNumber}/disposition`,
      { disposition, comment },
      { headers: { 'X-Admin-Token': token }, withCredentials: true }
    );
  },

  // ============================================================================
  // LEARNING SYSTEM - RFP Edit Capture & Signals
  // ============================================================================

  // Start an RFP editing session
  startEditSession: (procurementId) =>
    axios.post(`${API}/learning/session/start`, { procurement_id: procurementId }, { withCredentials: true }),

  // Complete an RFP editing session
  completeEditSession: (sessionId, totalEdits, fieldsEdited) =>
    axios.post(`${API}/learning/session/complete`, {
      session_id: sessionId,
      total_edits: totalEdits,
      fields_edited: fieldsEdited
    }, { withCredentials: true }),

  // Capture RFP field edits
  captureRfpEdits: (procurementId, category, edits) =>
    axios.post(`${API}/learning/edits`, {
      procurement_id: procurementId,
      category: category,
      edits: edits
    }, { withCredentials: true }),

  // Submit the RFP feedback dialog's response - one per procurement,
  // enforced server-side (409 on a second attempt for the same procurement).
  submitRfpFeedback: (data) =>
    axios.post(`${API}/learning/rfp-feedback`, data, { withCredentials: true }),

  // List all edit signals across all categories (admin only) - was previously
  // a per-category signal fetch that was never called anywhere in the app;
  // replaced with the admin listing endpoint since that's what's actually used.
  getLearningSignals: () =>
    axios.get(`${API}/learning/admin/signals`, { headers: getAuthHeader(), withCredentials: true }),

  // Get all learning signals (admin)
  getAllLearningSignals: () =>
    axios.get(`${API}/learning/signals`, { withCredentials: true }),

  // Delete a specific edit signal (admin only)
  deleteLearningSignal: (signalId) =>
    axios.delete(`${API}/learning/admin/signals/${signalId}`, { headers: getAuthHeader(), withCredentials: true }),

  // Clear accumulated learning data, optionally scoped to one category (admin only)
  resetLearning: (category = null) =>
    axios.post(`${API}/learning/admin/reset`, category ? { category } : {}, { headers: getAuthHeader(), withCredentials: true }),

  // Get learning system stats
  getLearningStats: () =>
    axios.get(`${API}/learning/stats`, { withCredentials: true }),

  // Trigger signal aggregation (admin only). Sends the admin bearer
  // header like its three siblings above - the endpoint is
  // Depends(require_admin) as of 2026-08-04; without this header the
  // admin panel's own Aggregate button would 401.
  aggregateLearningSignals: () =>
    axios.post(`${API}/learning/aggregate`, {}, { headers: getAuthHeader(), withCredentials: true })
};

export default api;
